import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import { format } from 'date-fns'
import PromoteModal from 'src/components/elements/modals/promote/PromoteModal'
import { Button } from 'src/components/ui'
import { IProperty } from 'src/interfaces/IProperty'
import {
  useDeletePropertyMutation,
  useGetPromotionPlansQuery,
  useGetUserQuery,
} from 'src/redux/api'
import { BinIcon, ChevronPrevIcon, EditIcon } from '@svgs'
import ChangeHistory from './ChangeHistory'

// the status colours of the design system
const statusClasses: Record<string, string> = {
  ACTIVE: 'bg-success-soft text-success',
  PENDING: 'bg-warning-soft text-warning',
  REJECTED: 'bg-danger-soft text-danger',
  COMPLETED: 'bg-muted-soft text-muted',
}
const CHIP = 'inline-flex items-center min-h-7 px-2.5 py-1 rounded-full text-xs font-bold leading-4'

// Owner controls on the listing page: the owner used to land on the bare
// public page with no way back and no actions (owner feedback 2026-08-05).
// Delete is armed by the first click and fires on the second one.
const OwnerPanel: FC<{ property: IProperty }> = ({ property }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: user } = useGetUserQuery(undefined)
  const { data: plansData } = useGetPromotionPlansQuery(undefined)
  const [openPromote, setOpenPromote] = useState(false)
  const [armedDelete, setArmedDelete] = useState(false)
  const [deleteProperty, { isLoading: isDeleting }] = useDeletePropertyMutation()

  useEffect(() => {
    if (!armedDelete) return
    const timer = setTimeout(() => setArmedDelete(false), 4000)
    return () => clearTimeout(timer)
  }, [armedDelete])

  const isOwner = !!user && user.id === property.user?.id
  if (!isOwner) return null

  const status = property.status || 'ACTIVE'
  const promotedUntil = property.promotedUntil ? new Date(property.promotedUntil) : null
  const isPromoted = !!promotedUntil && promotedUntil > new Date()
  // The self-serve purchase only makes sense for published listings and only
  // while monetization is switched on in the admin panel.
  const canPromote = Boolean(plansData?.enabled) && status === 'ACTIVE'

  const handleDelete = async () => {
    if (!armedDelete) {
      setArmedDelete(true)
      return
    }
    try {
      await deleteProperty(property.id).unwrap()
      navigate('/')
    } catch {
      setArmedDelete(false)
    }
  }

  return (
    <>
      <section className="flex flex-wrap items-center gap-2 p-3 bg-surface rounded-2xl shadow-card">
        {/* a desktop has "Назад" in the page's top row already */}
        <button
          onClick={() => navigate(-1)}
          className="md:hidden inline-flex items-center gap-2 h-9 px-3 rounded-xl text-sm font-bold text-ink fill-current hover:text-brand-ink transition-colors duration-200"
        >
          <span className="icon w-3 h-3 fill-current">
            <ChevronPrevIcon />
          </span>
          {t('owner_panel.back')}
        </button>
        <span className={clsx(CHIP, statusClasses[status] || statusClasses.ACTIVE)}>
          {t(`my_offers.status.${status.toLowerCase()}`)}
        </span>
        {isPromoted && promotedUntil && (
          <span className={clsx(CHIP, 'bg-brand-strong text-white')}>
            {t('promote.active_until', { date: format(promotedUntil, 'dd.MM.yyyy') })}
          </span>
        )}
        {typeof property.viewsCount === 'number' && (
          <span className={clsx(CHIP, 'bg-muted-soft text-muted')}>
            {t('views')}: {property.viewsCount}
          </span>
        )}
        <span className="mr-auto" />
        {canPromote && (
          <Button size="sm" onClick={() => setOpenPromote(true)}>
            {isPromoted ? t('promote.extend_button') : t('promote.button')}
          </Button>
        )}
        <Button
          size="sm"
          variant="outlined"
          iconAfter={<EditIcon />}
          onClick={() => navigate(`/edit-ad/${property.id}`)}
        >
          {t('edit_your_ad')}
        </Button>
        <Button
          size="sm"
          variant="danger"
          iconAfter={<BinIcon />}
          onClick={handleDelete}
          isLoading={isDeleting}
        >
          {armedDelete ? t('owner_panel.delete_confirm') : t('delete_the_ad')}
        </Button>
      </section>
      {/* Own-listing price history (C3). */}
      <ChangeHistory propertyId={property.id} />
      <PromoteModal
        open={openPromote}
        handleClose={() => setOpenPromote(false)}
        propertyId={property.id}
        title={property.addressString || ''}
        image={property.images?.[0]}
        price={property.price?.[0] ?? null}
      />
    </>
  )
}

export default OwnerPanel
